<template>
	<div>
		<div class="main">
			<div class="w-full flex justify-start" v-if="!isLoading">
				<div class="flex flex-shrink flex-col w-full" style="height: 800px; min-height: 500px; position: relative">
					<div class="font-bold text-red-300 pb-4" v-if="selectedFilters?.length > 0 && !isUserAllowedInLevel1">
						{{ $t('affected_customers_input') }} {{ customerCount }}
					</div>
					<BaseSelect
						class="w-1/2"
						ref="BaseSelect.filter"
						:label="$t('recipient_filter')"
						:options="filterOptions"
						v-model="selectedFilters"
						options-label="display"
						@input="validateValues"
						@removeSelect="removeSelectedOption"
						track-by="value"
						:required="false"
						:allow-empty="true"
						:multiple="true"
						:close-on-select-override="true"
					/>
					<div v-if="containsFilter('vehicle_manufacturer')" class="w-full">
						<div v-for="manufacturerInput in manufacturerInputs" :key="manufacturerInput.id" class="flex w-full">
							<div class="w-1/2">
								<AutocompleteInput
									:ref="`BaseInput.value.manufacturer.${manufacturerInput.id}`"
									v-model="manufacturerInput.input"
									:required="true"
									class="w-full"
									:label="$t('vehicle_manufacturer')"
									mask="AXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
									placeholder="Fahrzeughersteller"
									type="text"
									@input="updateManufacturer(manufacturerInput)"
									@selectValue="selectManufacturer(manufacturerInput)"
									:options="allManufacturers[manufacturerInput.id]"
									@blur="allManufacturers[manufacturerInput.id] = null"
								/>
							</div>

							<div v-if="manufacturerInputs.length > 1" class="mt-10">
								<font-awesome-icon
									:icon="['fas', 'trash']"
									class="w-5 h-5 ml-2 mr-2 cursor-pointer"
									@click="removeManufacturerInput(manufacturerInput)"
									size="lg"
								/>
							</div>
							<div class="w-1/2 pl-4 font-bold mt-10">
								<div
									v-if="
										!isUserAllowedInLevel1 &&
											filterResponse != null &&
											filterResponse.find(element => element.filter_key === 'vehicle_manufacturer' && element.value === manufacturerInput.input) !=
												null
									"
								>
									{{ $t('affected_customers_input') }}
									{{
										filterResponse.find(element => element.filter_key === 'vehicle_manufacturer' && manufacturerInput.input === element.value)
											.affected_customers
									}}
								</div>
							</div>
						</div>
					</div>
					<BaseButton
						@click="addNewManufacturerRow()"
						class="w-64 mb-4"
						v-if="
							containsFilter('vehicle_manufacturer') &&
								manufacturerInputs.length > 0 &&
								manufacturerInputs[manufacturerInputs.length - 1].input !== ''
						"
					>
						<font-awesome-icon :icon="['fa', 'plus']" class="w-5 h-5 pr-1" size="lg"></font-awesome-icon>{{ $t('add_more') }}</BaseButton
					>
					<div v-if="containsFilter('vehicle_model')" class="w-full">
						<div v-for="modelInput in modelInputs" :key="modelInput.id" class="flex w-full">
							<div class="w-1/2">
								<AutocompleteInput
									:ref="`BaseInput.value.model.${modelInput.id}`"
									v-model="modelInput.input"
									:required="true"
									class="w-full"
									:label="$t('vehicle_model')"
									mask="BXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
									placeholder="Fahrzeug-Modell"
									type="text"
									@input="updateModel(modelInput)"
									@selectValue="selectModel(modelInput)"
									:options="allModels[modelInput.id]"
									@blur="allModels[modelInput.id] = null"
								/>
							</div>
							<div v-if="modelInputs.length > 1" class="mt-10">
								<font-awesome-icon :icon="['fas', 'trash']" class="w-5 h-5 cursor-pointer" @click="removeModelInput(modelInput)" size="lg" />
							</div>
							<div class="w-1/2 pl-4 font-bold mt-10">
								<div
									v-if="
										!isUserAllowedInLevel1 &&
											filterResponse &&
											filterResponse.find(element => element.filter_key === 'vehicle_model' && element.value === modelInput.input) != null
									"
								>
									{{ $t('affected_customers_input') }}
									{{
										filterResponse.find(element => element.filter_key === 'vehicle_model' && modelInput.input === element.value).affected_customers
									}}
								</div>
							</div>
						</div>
					</div>

					<BaseButton
						@click="addNewModelRow()"
						class="w-64 mb-4"
						v-if="containsFilter('vehicle_model') && modelInputs.length > 0 && modelInputs[0].input !== ''"
					>
						<font-awesome-icon :icon="['fa', 'plus']" class="w-5 h-5 pr-1" size="lg"></font-awesome-icon>{{ $t('add_more') }}</BaseButton
					>
					<div class="w-full flex" v-if="containsFilter('vehicle_registration_first')">
						<div class="flex w-1/2 gap-4">
							<BaseSelect
								:required="true"
								:label="allApiFilters['vehicle_registration_first'][0].label"
								:options="registrationYears"
								v-model="selectedRegistrationFrom"
								:multiple="false"
								ref="BaseSelect.value.registrationFirstFrom"
								:allow-empty="false"
								@input="updateRegistrationFirstFrom"
								class="w-full"
							/>
							<BaseSelect
								:required="true"
								:label="allApiFilters['vehicle_registration_first'][1].label"
								:options="registrationYears"
								v-model="selectedRegistrationTo"
								:multiple="false"
								ref="BaseSelect.value.registrationFirstTo"
								:allow-empty="false"
								@input="updateRegistrationFirstTo"
								class="w-full"
							/>
						</div>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div
								v-if="
									!isUserAllowedInLevel1 &&
										filterResponse &&
										filterResponse.find(element => element.filter_key === 'vehicle_registration_first') != null
								"
							>
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_registration_first').affected_customers }}
							</div>
						</div>
					</div>
					<div v-if="containsFilter('vehicle_hu')" class="w-full flex">
						<div class="w-1/2 flex">
							<YearMonthInput
								class="md:mr-4 w-1/2"
								ref="BaseInput.value.huMonth"
								:required="true"
								:label="allApiFilters['vehicle_hu'][1].label"
								@input="updateHuFrom"
								:value="huFrom"
							/>
							<YearMonthInput
								class="w-1/2"
								ref="BaseInput.value.huMonth"
								:required="true"
								:label="allApiFilters['vehicle_hu'][0].label"
								@input="updateHuTo"
								:value="huTo"
							/>
						</div>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div v-if="!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_hu') != null">
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_hu').affected_customers }}
							</div>
						</div>
					</div>
					<div class="w-full flex" v-if="containsFilter('vehicle_exhaust_key')">
						<BaseSelect
							:required="true"
							:label="allApiFilters['vehicle_exhaust_key'][0].label"
							:options="exhaustOptions"
							v-model="selectedExhaustOptions"
							:multiple="true"
							ref="BaseSelect.value.exhaustKey"
							:allow-empty="false"
							class="w-1/2"
							@input="updateExhaust"
						/>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div
								v-if="
									!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_exhaust_key') != null
								"
							>
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_exhaust_key').affected_customers }}
							</div>
						</div>
					</div>

					<div class="w-full flex" v-if="containsFilter('vehicle_fuel')">
						<BaseSelect
							:required="true"
							:label="allApiFilters['vehicle_fuel'][0].label"
							:options="fuelOptions"
							v-model="selectedFuelOptions"
							:multiple="true"
							ref="BaseSelect.value.fuel"
							:allow-empty="false"
							@input="updateFuelType"
							class="w-1/2"
						/>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div v-if="!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_fuel') != null">
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_fuel').affected_customers }}
							</div>
						</div>
					</div>
					<div class="flex" v-if="containsFilter('vehicle_kilometrage')">
						<div class="w-1/2 flex gap-4">
							<BaseInputNumber
								ref="BaseInput.value.kilometrageFrom"
								:required="true"
								:label="allApiFilters['vehicle_kilometrage'][1].label"
								type="tel"
								class="w-1/2 mb-4"
								base-input-class="mb-0"
								v-model="mileageFrom"
								@input="updateMileageFrom"
								:min-number="1"
								:error-message="$t('km_smaller_one_hint')"
								:max-length="6"
							/>
							<BaseInputNumber
								ref="BaseInput.value.kilometrageTo"
								:required="true"
								:label="allApiFilters['vehicle_kilometrage'][0].label"
								type="tel"
								class="w-1/2 mb-4"
								base-input-class="mb-0"
								v-model="mileageTo"
								@input="updateMileageTo"
								:min-number="1"
								:error-message="$t('km_smaller_one_hint')"
								:max-length="6"
							/>
						</div>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div
								v-if="
									!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_kilometrage') != null
								"
							>
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_kilometrage').affected_customers }}
							</div>
						</div>
					</div>
					<div class="flex" v-if="containsFilter('vehicle_kw')">
						<div class="w-1/2 flex gap-4">
							<BaseInputNumber
								ref="BaseInput.value.kwFrom"
								:required="true"
								:label="allApiFilters['vehicle_kw'][1].label"
								type="tel"
								class="w-1/2"
								base-input-class="mb-0"
								v-model="kwFrom"
								@input="updateKWFrom"
								:error-message="$t('km_smaller_one')"
								:max-length="3"
							/>
							<BaseInputNumber
								ref="BaseInput.value.kwTo"
								:required="true"
								:label="allApiFilters['vehicle_kw'][0].label"
								type="tel"
								class="w-1/2"
								base-input-class="mb-0"
								v-model="kwTo"
								@input="updateKWTo"
								:error-message="$t('km_smaller_one')"
								:max-length="3"
							/>
						</div>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div v-if="!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_kw') != null">
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_kw').affected_customers }}
							</div>
						</div>
					</div>
					<div class="flex mt-4" v-if="containsFilter('vehicle_displacement')">
						<div class="w-1/2 flex gap-4">
							<BaseInputNumber
								ref="BaseInput.value.displacementFrom"
								:required="true"
								:label="allApiFilters['vehicle_displacement'][1].label"
								type="tel"
								class="w-1/2"
								@input="updateDisplacementFrom"
								base-input-class="mb-0"
								v-model="displacementFrom"
								:error-message="$t('displacement_smaller_one')"
								:max-length="4"
							/>
							<BaseInputNumber
								ref="BaseInput.value.displacementTo"
								:required="true"
								:label="allApiFilters['vehicle_displacement'][0].label"
								type="tel"
								class="w-1/2"
								@input="updateDisplacementTo"
								base-input-class="mb-0"
								v-model="displacementTo"
								:error-message="$t('displacement_smaller_one')"
								:max-length="4"
							/>
						</div>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div
								v-if="
									!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'vehicle_displacement') != null
								"
							>
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'vehicle_displacement').affected_customers }}
							</div>
						</div>
					</div>
					<div class="flex" v-if="containsFilter('shop_visit')">
						<BaseSelect
							:required="true"
							:label="allApiFilters['shop_visit'][0].label"
							:options="activeMonthsOptions"
							v-model="selectedActiveMonth"
							:multiple="false"
							ref="BaseSelect.value.activeVehicles"
							:allow-empty="false"
							@input="updateActiveMonth"
							class="w-1/2"
						/>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div v-if="!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'shop_visit') != null">
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'shop_visit').affected_customers }}
							</div>
						</div>
					</div>
					<div class="flex mt-4" v-if="containsFilter('no_shop_visit')">
						<BaseSelect
							:required="true"
							:label="allApiFilters['no_shop_visit'][0].label"
							:options="activeMonthsOptions"
							v-model="selectedInactiveMonth"
							:multiple="false"
							ref="BaseSelect.value.inactiveVehicles"
							:allow-empty="false"
							@input="updateInActiveMonth"
							class="w-1/2"
						/>
						<div class="w-1/2 pl-4 font-bold mt-10">
							<div v-if="!isUserAllowedInLevel1 && filterResponse && filterResponse.find(element => element.filter_key === 'no_shop_visit') != null">
								{{ $t('affected_customers_input') }}
								{{ filterResponse.find(element => element.filter_key === 'no_shop_visit').affected_customers }}
							</div>
						</div>
					</div>
				</div>
			</div>
			<LoadingSpinner v-else />
		</div>
	</div>
</template>

<script>
import Validate from '@/mixins/Validate';
import Authentication from '@/mixins/Authentication';
import BaseInput from '@/components/forms/inputs/BaseInput.vue';
import BaseSelect from '@/components/forms/inputs/BaseSelect.vue';
import AutocompleteInput from '@/components/forms/inputs/AutocompleteInput.vue';
import BaseInputNumber from '@/components/forms/inputs/BaseInputNumber.vue';
import regex from '@/helpers/Regex';
import VehicleRepository from '@/api/repositories/VehicleRepository';
import SpecialOfferRepository from '@/api/repositories/SpecialOfferRepository';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import YearMonthInput from '@/components/forms/inputs/YearMonthInput.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

export default {
	name: 'AdFilters',
	components: { LoadingSpinner, YearMonthInput, BaseButton, BaseInputNumber, AutocompleteInput, BaseSelect, BaseInput },
	mixins: [Validate, Authentication],
	computed: {
		regex() {
			return regex;
		},
	},
	data() {
		return {
			isLoading: false,
			customerCount: 0,
			filterOptions: [],
			selectedFilters: [],
			selectedManufacturer: null,
			manufacturerInputs: [{ id: 1, input: '' }],
			allManufacturers: [],
			updateManufacturerTimeoutId: null,
			selectedModel: null,
			modelInputs: [{ id: 1, input: '' }],
			allModels: [],
			updateModelTimeoutId: null,
			zipCode: '',
			huFrom: null,
			huTo: null,
			fuelOptions: [
				{ display: 'Diesel', value: 'Diesel' },
				{ display: 'Benzin', value: 'Benzin' },
				{ display: 'Strom', value: 'Strom' },
				{ display: 'Hybrid', value: 'Hybrid' },
				{ display: 'Benzin LPG', value: 'Benzin LPG' },
				{ display: 'Wasserstoff', value: 'Wasserstoff' },
				{ display: 'Plug-in Hybrid', value: 'Plug-in Hybrid' },
			],
			selectedFuelOptions: [],
			updateFuelTimeoutId: null,
			exhaustOptions: [
				{ display: 'Euro2', value: 'Euro2' },
				{ display: 'Euro3', value: 'Euro3' },
				{ display: 'Euro4', value: 'Euro4' },
				{ display: 'Euro5', value: 'Euro5' },
				{ display: 'Euro6', value: 'Euro6' },
				{ display: 'Euro6dtemp', value: 'Euro6dtemp' },
				{ display: 'Elektro', value: 'Elektro' },
			],
			selectedExhaustOptions: [],
			updateExhaustTimeoutId: null,
			mileageFrom: null,
			mileageTo: null,
			updateMileageTimeoutId: null,
			licensePlateCity: '',
			registrationYears: [],
			selectedRegistrationFrom: null,
			selectedRegistrationTo: null,
			kwFrom: null,
			kwTo: null,
			updateKwTimeoutId: null,
			displacementFrom: null,
			displacementTo: null,
			updateDisplacementTimeoutId: null,
			activeMonthsOptions: [],
			selectedActiveMonth: null,
			selectedInactiveMonth: null,
			apiFilters: [],
			allApiFilters: [],
			filterResponse: null,
		};
	},
	methods: {
		removeSelectedOption(event) {
			this.apiFilters = this.apiFilters.filter(element => element.special_offer_filter.key !== event.value);

			if (!this.isUserAllowedInLevel1) {
				this.fetchCustomerCountByFilter();
			}
		},
		removeManufacturerInput(manufacturerInput) {
			if (this.manufacturerInputs.length > 1) {
				this.manufacturerInputs.splice(
					this.manufacturerInputs.findIndex(element => element.id === manufacturerInput.id),
					1,
				);
			}
		},
		removeModelInput(modelInput) {
			if (this.modelInputs.length > 1) {
				this.modelInputs.splice(
					this.modelInputs.findIndex(element => element.id === modelInput.id),
					1,
				);
			}
		},
		addNewManufacturerRow() {
			const highestId = this.manufacturerInputs.reduce((max, item) => {
				return item.id > max ? item.id : max;
			}, 0);

			const newId = highestId + 1;
			this.manufacturerInputs.push({ input: '', id: newId });
		},
		addNewModelRow() {
			const highestId = this.modelInputs.reduce((max, item) => {
				return item.id > max ? item.id : max;
			}, 0);

			const newId = highestId + 1;
			this.modelInputs.push({ input: '', id: newId });
		},
		containsFilter(filter) {
			let returnValue = false;
			if (this.selectedFilters != null) {
				this.selectedFilters.forEach(selectedFilter => {
					if (selectedFilter.value === filter) {
						returnValue = true;
					}
				});
			} else {
				returnValue = false;
			}
			return returnValue;
		},
		validateValues() {
			if (this.selectedFilters.find(x => x.value === 'no_shop_visit') && this.selectedFilters.find(x => x.value === 'shop_visit')) {
				this.$swal(this.$t('general.attention').toString(), this.$t('only_one_of_both_filters_allowed').toString(), 'warning');
				this.selectedFilters.splice(
					this.selectedFilters.findIndex(x => x.value === 'no_shop_visit'),
					1,
				);
			}
		},
		updateManufacturer(manufacturerInput) {
			this.selectedManufacturer = null;
			clearTimeout(this.updateManufacturerTimeoutId);
			this.updateManufacturerTimeoutId = setTimeout(() => {
				new VehicleRepository().getManufacturerNames(manufacturerInput.input).then(response => {
					this.allManufacturers[manufacturerInput.id] = response.data?.map(manufacturer => manufacturer.name);
				});
				this.selectManufacturer(manufacturerInput);
			}, 300);
		},
		updateModel(modelInput) {
			this.allModels[modelInput.id] = null;
			clearTimeout(this.updateModelTimeoutId);
			this.updateModelTimeoutId = setTimeout(() => {
				new VehicleRepository().getModelNames(this.modelInput, modelInput.input).then(response => {
					this.allModels[modelInput.id] = response.data?.map(model => model.name);
				});
				this.selectModel(modelInput);
			}, 300);
		},
		selectManufacturer(manufacturer) {
			this.selectedManufacturer = manufacturer.input;
			clearTimeout(this.updateManufacturerTimeoutId);
			this.setApiFilters(this.allApiFilters['vehicle_manufacturer'][0], manufacturer.input, manufacturer.id);
			if (!this.isUserAllowedInLevel1) {
				this.fetchCustomerCountByFilter();
			}
		},
		selectModel(model) {
			clearTimeout(this.updateModelTimeoutId);
			this.setApiFilters(this.allApiFilters['vehicle_model'][0], model.input, model.id);
			if (!this.isUserAllowedInLevel1) {
				this.fetchCustomerCountByFilter();
			}
		},
		updateHuFrom(value) {
			this.huFrom = value;
			this.setApiFilters(this.allApiFilters['vehicle_hu'][0], this.huFrom, 1);
			if (this.huTo && this.huFrom) {
				if (this.huFrom > this.huTo) {
					this.$swal(this.$t('general.attention').toString(), this.$t('hu_date_error').toString(), 'warning');
				} else {
					if (!this.isUserAllowedInLevel1) {
						this.fetchCustomerCountByFilter();
					}
				}
			}
		},
		updateHuTo(value) {
			this.huTo = value;
			this.setApiFilters(this.allApiFilters['vehicle_hu'][1], this.huTo, 2);
			if (this.huTo && this.huFrom) {
				if (this.huFrom > this.huTo) {
					this.$swal(this.$t('general.attention').toString(), this.$t('hu_date_error').toString(), 'warning');
				} else {
					if (!this.isUserAllowedInLevel1) {
						this.fetchCustomerCountByFilter();
					}
				}
			}
		},
		updateRegistrationFirstFrom(value) {
			this.setApiFilters(this.allApiFilters['vehicle_registration_first'][0], this.selectedRegistrationFrom.value, 1);
			if (this.selectedRegistrationFrom && this.selectedRegistrationTo) {
				if (this.selectedRegistrationFrom.value > this.selectedRegistrationTo.value) {
					this.$swal(this.$t('general.attention').toString(), this.$t('last_before_first_registration_error').toString(), 'warning');
				} else {
					if (!this.isUserAllowedInLevel1) {
						this.fetchCustomerCountByFilter();
					}
				}
			}
		},
		updateRegistrationFirstTo(value) {
			this.setApiFilters(this.allApiFilters['vehicle_registration_first'][1], this.selectedRegistrationTo.value, 2);
			if (this.selectedRegistrationFrom && this.selectedRegistrationTo) {
				if (this.selectedRegistrationFrom.value > this.selectedRegistrationTo.value) {
					this.$swal(this.$t('general.attention').toString(), this.$t('last_before_first_registration_error').toString(), 'warning');
				} else {
					if (!this.isUserAllowedInLevel1) {
						this.fetchCustomerCountByFilter();
					}
				}
			}
		},
		updateMileageFrom(value) {
			clearTimeout(this.updateMileageTimeoutId);
			this.updateMileageTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_kilometrage'][0], this.mileageFrom, 1);
				if (this.mileageFrom && this.mileageTo) {
					if (this.mileageFrom > this.mileageTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('mileage_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateMileageTo(value) {
			clearTimeout(this.updateMileageTimeoutId);
			this.updateMileageTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_kilometrage'][1], this.mileageTo, 2);
				if (this.mileageFrom && this.mileageTo) {
					if (this.mileageFrom > this.mileageTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('mileage_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateKWFrom() {
			clearTimeout(this.updateKwTimeoutId);
			this.updateKwTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_kw'][0], this.kwFrom, 1);
				if (this.kwFrom && this.kwTo) {
					if (this.kwFrom > this.kwTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('kw_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateKWTo() {
			clearTimeout(this.updateKwTimeoutId);
			this.updateKwTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_kw'][1], this.kwTo, 2);
				if (this.kwFrom && this.kwTo) {
					if (this.kwFrom > this.kwTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('kw_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateDisplacementFrom() {
			clearTimeout(this.updateDisplacementTimeoutId);
			this.updateDisplacementTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_displacement'][0], this.displacementFrom, 1);
				if (this.displacementFrom && this.displacementTo) {
					if (this.displacementFrom > this.displacementTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('displacement_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateDisplacementTo() {
			clearTimeout(this.updateDisplacementTimeoutId);
			this.updateDisplacementTimeoutId = setTimeout(() => {
				this.setApiFilters(this.allApiFilters['vehicle_displacement'][1], this.displacementTo, 2);
				if (this.displacementFrom && this.displacementTo) {
					if (this.displacementFrom > this.displacementTo) {
						this.$swal(this.$t('general.attention').toString(), this.$t('displacement_number_error').toString(), 'warning');
					} else {
						if (!this.isUserAllowedInLevel1) {
							this.fetchCustomerCountByFilter();
						}
					}
				}
			}, 1000);
		},
		updateFuelType() {
			clearTimeout(this.updateFuelTimeoutId);
			this.updateFuelTimeoutId = setTimeout(() => {
				this.apiFilters = this.apiFilters.filter(element => element.special_offer_filter.key !== 'vehicle_fuel');
				//TODO: check if multiple selected
				this.selectedFuelOptions.forEach((fuelOption, index) => {
					const newIndex = index + 1;
					this.setApiFilters(this.allApiFilters['vehicle_fuel'][0], fuelOption.value, newIndex);
				});
				if (!this.isUserAllowedInLevel1) {
					this.fetchCustomerCountByFilter();
				}
			}, 1000);
		},
		updateExhaust() {
			clearTimeout(this.updateExhaustTimeoutId);
			this.updateExhaustTimeoutId = setTimeout(() => {
				this.apiFilters = this.apiFilters.filter(element => element.special_offer_filter.key !== 'vehicle_exhaust_key');
				this.selectedExhaustOptions.forEach((exhaustOption, index) => {
					const newIndex = index + 1;
					this.setApiFilters(this.allApiFilters['vehicle_exhaust_key'][0], exhaustOption.value, newIndex);
				});
				this.fetchCustomerCountByFilter();
			}, 1000);
		},
		updateActiveMonth() {
			this.setApiFilters(this.allApiFilters['shop_visit'][0], this.selectedActiveMonth.value, 1);
			this.fetchCustomerCountByFilter();
		},
		updateInActiveMonth() {
			this.setApiFilters(this.allApiFilters['no_shop_visit'][0], this.selectedInactiveMonth.value, 1);
			this.fetchCustomerCountByFilter();
		},
		setApiFilters(filter, value, internId = 1) {
			this.apiFilters = this.apiFilters.filter(element => !(element.special_offer_filter.key === filter.key && element.internId === internId));

			this.apiFilters.push({
				special_offer_filter: {
					id: filter.id,
					key: filter.key,
					data_type: filter.data_type,
					operator: filter.operator,
				},
				link_data: {
					value: value,
					logical_operator: 'AND',
				},
				internId: internId,
			});
		},
		fetchCustomerCountByFilter() {
			new SpecialOfferRepository()
				.applyFilters({
					filters: this.apiFilters,
				})
				.then(response => {
					this.filterResponse = response.data.individual_counts;
					this.customerCount = response.data.total_count;
				})
				.catch(e => {});
		},
		save() {
			if (this.validate()) {
				this.$store.commit('clearMarketingAds');
				this.$store.commit('setConfirmLeaving', false);
				this.$store.commit('setMarketingFilters', {
					filters: this.apiFilters,
					affectedCustomers: this.customerCount,
					filterResponse: this.filterResponse,
				});
				this.$router.go(-1);
			} else {
				this.$swal(this.$t('general.error').toString(), this.$t('fill_in_fields_or_remove_filter').toString(), 'error');
			}
		},
		setStoredParameters() {
			const filters = this.$store.getters.getMarketingFilters;
			if (filters.length > 0) {
				this.apiFilters = filters;
				this.customerCount = this.$store.getters.getAffectedCustomers;
				this.filterResponse = this.$store.getters.getFilterResponse;

				this.apiFilters.forEach(filter => {
					if (filter.special_offer_filter.key === 'vehicle_manufacturer') {
						this.manufacturerInputs = [];
					}
					if (filter.special_offer_filter.key === 'vehicle_model') {
						this.modelInputs = [];
					}

					if (!this.selectedFilters.find(element => element.value === filter.special_offer_filter.key)) {
						this.selectedFilters.push(this.filterOptions.find(apiFilter => apiFilter.value === filter.special_offer_filter.key));
					}
				});
				this.setStoredParametersInFields(filters);
			} else {
				this.isLoading = false;
			}
		},
		setStoredParametersInFields(savedFilters) {
			savedFilters.forEach(element => {
				switch (element.special_offer_filter.key) {
					case 'vehicle_manufacturer':
						{
							this.manufacturerInputs.push({ input: element.link_data.value, id: element.internId });
						}
						break;
					case 'vehicle_model':
						{
							this.modelInputs.push({ input: element.link_data.value, id: element.internId });
						}
						break;
					case 'vehicle_fuel':
						{
							this.selectedFuelOptions.push(this.fuelOptions.find(fuelOption => fuelOption.value === element.link_data.value));
						}
						break;
					case 'vehicle_exhaust_key':
						{
							this.selectedExhaustOptions.push(this.exhaustOptions.find(exhaustOption => exhaustOption.value === element.link_data.value));
						}
						break;
					case 'vehicle_kilometrage':
						{
							if (element.internId === 1) {
								this.mileageFrom = element.link_data.value;
							} else {
								this.mileageTo = element.link_data.value;
							}
						}
						break;
					case 'vehicle_kw':
						{
							if (element.internId === 1) {
								this.kwFrom = element.link_data.value;
							} else {
								this.kwTo = element.link_data.value;
							}
						}
						break;
					case 'vehicle_hu':
						{
							if (element.internId === 1) {
								this.huFrom = element.link_data.value;
							} else {
								this.huTo = element.link_data.value;
							}
						}
						break;
					case 'vehicle_registration_first':
						{
							if (element.internId === 1) {
								this.selectedRegistrationFrom = this.registrationYears.find(
									registrationYear => Number(registrationYear.value) === Number(element.link_data.value),
								);
							} else {
								this.selectedRegistrationTo = this.registrationYears.find(
									registrationYear => Number(registrationYear.value) === Number(element.link_data.value),
								);
							}
						}
						break;
					case 'vehicle_displacement':
						{
							if (element.internId === 1) {
								this.displacementFrom = element.link_data.value;
							} else {
								this.displacementTo = element.link_data.value;
							}
						}
						break;
					case 'shop_visit':
						{
							this.selectedActiveMonth = this.activeMonthsOptions.find(monthOption => Number(monthOption.value) === Number(element.link_data.value));
						}
						break;
					case 'no_shop_visit':
						{
							this.selectedInactiveMonth = this.activeMonthsOptions.find(
								monthOption => Number(monthOption.value) === Number(element.link_data.value),
							);
						}
						break;
				}
			});
			this.isLoading = false;
		},
	},
	mounted() {
		this.$store.commit('setConfirmLeaving', false);
		this.isLoading = true;
		new SpecialOfferRepository()
			.getFilters()
			.then(response => {
				this.allApiFilters = response.data;
				const whiteLabels = ['vehicle_registration_first', 'vehicle_hu', 'vehicle_kilometrage', 'vehicle_kw', 'vehicle_displacement'];
				Object.values(response.data).forEach(values => {
					if (whiteLabels.includes(values[0].key)) {
						const name = values[0].label.split(' ')[0];
						this.filterOptions.push({ display: name, value: values[0].key });
					} else {
						this.filterOptions.push({ display: values[0].label, value: values[0].key });
					}
				});
				// if(this.$route.params.id != null){
				//
				// }

				this.setStoredParameters();
			})
			.catch(e => {
				console.log(e);
			});

		for (let year = new Date().getFullYear(); year >= 1950; year--) {
			this.registrationYears.push({ display: year, value: year });
		}

		for (let i = 1; i < 13; i++) {
			this.activeMonthsOptions.push({ display: i === 1 ? i + ' Monat' : i + ' Monate', value: i });
		}
	},
};
</script>

<style scoped></style>
